import { outcomeProbability } from '@/features/brazil-elections/utils'
import { deriveMarketUi } from '@/features/market/utils/marketType'
import { getPositionLabels } from '@/features/market/utils/positionLabels'
import { US_STATES, US_STATE_BY_NAME } from './usStates'

/* Same party hues as the home midterm banner (useSenateControl), so the map,
   the banner and the scoreboard can never drift apart. */
export const PARTY_COLORS = { democratic: '#3340F0', republican: '#E03131' }
export const PARTY_LABEL_COLORS = { democratic: '#97A3FF', republican: '#FF8F8F' }

const PARTY_PATTERNS = [
  { party: 'democratic', pattern: /democrat/i },
  { party: 'republican', pattern: /republican/i },
]

/* Marquee order inside a state: a Senate race headlines the state, then the
   governor's, then House districts (several can share one state). */
const RACE_PRIORITY = { senate: 0, governor: 1, house: 2, other: 3 }

export const raceTypeOf = title => {
  const text = String(title || '')
  if (/senate/i.test(text)) return 'senate'
  if (/governor/i.test(text)) return 'governor'
  if (/congressional district|house of representatives/i.test(text)) return 'house'
  return 'other'
}

/** The state a market belongs to, read off its title; null for national races. */
export const stateOfMarket = market => {
  const title = String(market?.title || '').toLowerCase()
  /* Longest name first so "West Virginia" wins over "Virginia", and so on. */
  const name = Object.keys(US_STATE_BY_NAME)
    .sort((a, b) => b.length - a.length)
    .find(candidate => title.includes(candidate))
  return name ? US_STATE_BY_NAME[name] : null
}

/**
 * Democratic / Republican quotes of one market, each `{ party, label,
 * probability }` with probability 0–1 or null when unquoted. Two market
 * shapes are understood, the same pair `useSenateControl` reads for the home
 * banner (see docs/MARKET_UI_TYPES.md):
 *
 * - Multi-outcome: each party is a physical outcome, matched by title.
 * - Mono custom (Type A): one physical outcome whose long/short position
 *   labels name the parties; the price quotes the long side, so the short
 *   side is its complement.
 */
export const partySides = (market, currencyId) => {
  const outcomes = Array.isArray(market?.outcomes) ? market.outcomes : []

  const fromOutcomes = PARTY_PATTERNS.map(({ party, pattern }) => {
    const outcome = outcomes.find(candidate => !candidate?.disabled && pattern.test(String(candidate?.title)))
    return outcome ? { party, label: outcome.title, probability: outcomeProbability(outcome, market, currencyId) } : null
  }).filter(Boolean)
  if (fromOutcomes.length === 2) return fromOutcomes

  const ui = deriveMarketUi(market)
  const outcome = ui.isMono && ui.needsVirtualOutcomes && !outcomes[0]?.disabled ? outcomes[0] : null
  if (!outcome) return null

  const { longLabel, shortLabel } = getPositionLabels(outcome, market)
  const longProbability = outcomeProbability(outcome, market, currencyId)
  const shortProbability = longProbability == null ? null : Math.min(1, Math.max(0, 1 - longProbability))
  const fromPositions = PARTY_PATTERNS.map(({ party, pattern }) => {
    const isLong = pattern.test(String(longLabel))
    const isShort = !isLong && pattern.test(String(shortLabel))
    if (!isLong && !isShort) return null
    return { party, label: isLong ? longLabel : shortLabel, probability: isLong ? longProbability : shortProbability }
  }).filter(Boolean)
  return fromPositions.length === 2 ? fromPositions : null
}

/**
 * Adapts one feed market into the race the map shows, or null when it carries
 * no usable Democratic/Republican pair (candidate-named markets stay in the
 * grid below the map instead of guessing a party for them).
 */
export const toMidtermRace = (market, currencyId) => {
  const sides = partySides(market, currencyId)
  if (!sides) return null

  const [democratic, republican] = sides
  const leader =
    democratic.probability != null && republican.probability != null
      ? democratic.probability >= republican.probability
        ? democratic
        : republican
      : null
  const volume = Number(market.volume_real_money || market.volume_play_money) || 0

  return {
    market,
    id: market.id,
    title: market.title,
    raceType: raceTypeOf(market.title),
    state: stateOfMarket(market),
    democratic,
    republican,
    leader,
    /* Points between the two sides; 0 is a dead heat. Null while unquoted. */
    margin:
      democratic.probability != null && republican.probability != null
        ? Math.abs(democratic.probability - republican.probability) * 100
        : null,
    volume,
  }
}

/**
 * Groups races by state. Each state keeps its marquee race first (Senate >
 * governor > House, then volume), which is also the race that colours its
 * tile. National races (Senate / House control) come back separately.
 */
export const groupRacesByState = races => {
  const byState = new Map()
  const national = []

  races.filter(Boolean).forEach(race => {
    if (!race.state) {
      national.push(race)
      return
    }
    const list = byState.get(race.state.code) || []
    list.push(race)
    byState.set(race.state.code, list)
  })

  byState.forEach(list =>
    list.sort((a, b) => RACE_PRIORITY[a.raceType] - RACE_PRIORITY[b.raceType] || b.volume - a.volume)
  )
  national.sort((a, b) => RACE_PRIORITY[a.raceType] - RACE_PRIORITY[b.raceType] || b.volume - a.volume)

  return { byState, national }
}

/**
 * The order the spotlight tour visits states in: closest marquee race first,
 * so the auto-highlight spends its time on the races that can still flip.
 */
export const tourOrder = byState =>
  US_STATES.filter(tile => byState.has(tile.code))
    .map(tile => ({ tile, marquee: byState.get(tile.code)[0] }))
    .sort((a, b) => (a.marquee.margin ?? 101) - (b.marquee.margin ?? 101) || b.marquee.volume - a.marquee.volume)
    .map(entry => entry.tile.code)

/** Fill strength for a state: a coin-flip race is faint, a runaway is solid. */
export const tileOpacity = race => {
  if (!race?.leader?.probability) return 0.16
  return Math.min(1, 0.3 + (race.leader.probability - 0.5) * 1.5)
}
